import {
	colors,
	intl,
	MaybeUserProfile,
	ui,
	userApi,
	UUID,
} from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode, useEffect, useRef } from 'react'
import { StageControls } from 'room/common/stage-surface'
import ProfileAvatar from 'users/profile-avatar'
import { PollEyebrow, PollStageCard } from '../stage/poll-stage'

export type StageAnswer = {
	text: string
	/** Who wrote it, once their profile has arrived. */
	author?: MaybeUserProfile
}

export type StageQuestion = {
	id: UUID
	text: string
	answers: StageAnswer[]
}

/** A column per question, side by side; past three they scroll sideways. */
const Columns = styled.div`
	display: grid;
	flex: 1 1 auto;
	gap: 24px;
	grid-auto-columns: minmax(340px, 1fr);
	grid-auto-flow: column;
	margin-top: 14px;
	min-height: 0;
	overflow-x: auto;

	${ui.responsive.desktopFHD} {
		gap: 32px;
		margin-top: 18px;
	}
`

const Column = styled.section`
	background: ${colors.dustyWhite};
	border-radius: 14px;
	display: flex;
	flex-direction: column;
	min-height: 0;
	padding: 24px;

	${ui.responsive.desktopFHD} {
		padding: 32px;
	}
`

/** The chat's header: the question over a line, the messages under it. */
const ColumnHead = styled.div`
	align-items: baseline;
	border-bottom: 1px solid ${colors.heather30};
	display: flex;
	flex: none;
	flex-wrap: wrap;
	gap: 8px 20px;
	justify-content: space-between;
	margin-bottom: 20px;
	padding-bottom: 20px;

	${ui.responsive.desktopFHD} {
		margin-bottom: 24px;
		padding-bottom: 24px;
	}
`

/** One question is the card's heading; several share the room. */
const Question = styled.h2<{ $single: boolean }>`
	color: ${colors.blackish};
	font-size: ${props => (props.$single ? 28 : 22)}px;
	font-weight: 700;
	line-height: 1.2;
	margin: 0;
	overflow-wrap: anywhere;

	${ui.responsive.desktop1440} {
		font-size: ${props => (props.$single ? 34 : 26)}px;
	}

	${ui.responsive.desktopFHD} {
		font-size: ${props => (props.$single ? 44 : 34)}px;
	}
`

const Count = styled.span`
	color: ${colors.warmGrey};
	flex: none;
	font-size: 16px;
	font-variant-numeric: tabular-nums;

	${ui.responsive.desktop1440} {
		font-size: 18px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 24px;
	}
`

/** The online list's chat, one message under another, in type for the room. */
const Answers = styled.ul`
	align-items: flex-start;
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	gap: 8px;
	list-style: none;
	margin: 0;
	min-height: 0;
	overflow-y: auto;
	padding: 0;

	${ui.responsive.desktop1440} {
		gap: 10px;
	}

	${ui.responsive.desktopFHD} {
		gap: 12px;
	}
`

/**
 * As wide as what it says, as a chat message is, and as tall: little room
 * above and below the line.
 */
const Bubble = styled.li`
	align-items: flex-start;
	background: ${colors.heather10};
	border-radius: 10px;
	display: flex;
	flex: none;
	gap: 10px;
	max-width: 100%;
	padding: 6px 14px 6px 8px;

	${ui.responsive.desktop1440} {
		gap: 12px;
		padding: 7px 16px 7px 10px;
	}

	${ui.responsive.desktopFHD} {
		border-radius: 14px;
		gap: 14px;
		padding: 9px 22px 9px 12px;
	}
`

/** The height of the message's first line, so a one-line bubble is no taller than its text. */
const Avatar = styled(ProfileAvatar)`
	border-radius: 50%;
	flex: none;
	height: 28px;
	width: 28px;

	${ui.responsive.desktop1440} {
		height: 30px;
		width: 30px;
	}

	${ui.responsive.desktopFHD} {
		height: 42px;
		width: 42px;
	}
`

const Message = styled.p`
	color: ${colors.blackish};
	font-size: 20px;
	line-height: 1.4;
	margin: 0;
	min-width: 0;
	overflow-wrap: anywhere;
	white-space: pre-wrap;

	${ui.responsive.desktop1440} {
		font-size: 22px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 30px;
	}
`

const Name = styled.strong`
	font-weight: 700;
`

const Empty = styled.p`
	color: ${colors.warmGrey};
	font-size: 20px;
	line-height: 1.4;
	margin: 0;

	${ui.responsive.desktopFHD} {
		font-size: 28px;
	}
`

const Field = styled.div`
	flex: none;
	margin-top: 16px;
`

/** The newest answer in view as it arrives, as the online list does. */
const useFollowNewest = (count: number) => {
	const ref = useRef<HTMLUListElement>(null)
	useEffect(() => {
		const list = ref.current
		if (list && typeof list.scrollTo === 'function') {
			list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' })
		}
	}, [count])
	return ref
}

const QuestionColumn: FC<{
	question: StageQuestion
	single: boolean
	answerField?: ReactNode
}> = ({ question, single, answerField }) => {
	const listRef = useFollowNewest(question.answers.length)
	const headingId = `list-polling-stage-${question.id}`
	return (
		<Column aria-labelledby={headingId}>
			<ColumnHead>
				<Question id={headingId} $single={single}>
					{question.text}
				</Question>
				<Count>
					<intl.Translate count={question.answers.length}>
						polling.stage.list.count
					</intl.Translate>
				</Count>
			</ColumnHead>
			{question.answers.length ? (
				<Answers ref={listRef}>
					{question.answers.map((answer, index) => {
						const name = answer.author
							? userApi.getDisplayName(answer.author)
							: ''
						return (
							<Bubble key={index}>
								<Avatar profile={answer.author} />
								<Message>
									{name ? <Name>{name}: </Name> : null}
									{answer.text}
								</Message>
							</Bubble>
						)
					})}
				</Answers>
			) : (
				<Empty>
					<intl.Translate>polling.stage.list.empty</intl.Translate>
				</Empty>
			)}
			{answerField ? (
				<StageControls>
					<Field>{answerField}</Field>
				</StageControls>
			) : null}
		</Column>
	)
}

type Props = {
	name: string
	questions: StageQuestion[]
	/** The trainer's own answer to a question: on their window, never on the projector. */
	answerField?: (questionId: UUID) => ReactNode
}

/**
 * A list poll on the in-person stage: the online list's chat - a column for
 * each question, its answers one message under another - in type that reads
 * from the back of the room, the newest kept in view. The room answers on its
 * own devices. The online card is `ListPollingChat`.
 */
export const ListPollingStageView: FC<Props> = ({
	name,
	questions,
	answerField,
}) => (
	<PollStageCard testId="list_polling.stage">
		<PollEyebrow>
			<intl.Translate name={name}>polling.stage.list.eyebrow</intl.Translate>
		</PollEyebrow>
		<Columns>
			{questions.map(question => (
				<QuestionColumn
					key={question.id}
					question={question}
					single={questions.length === 1}
					answerField={answerField?.(question.id)}
				/>
			))}
		</Columns>
	</PollStageCard>
)
